import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { C, mono, serif, sans, EASE, scrollTo } from "@/lib/theme";
import { useClock } from "@/lib/useClock";
import { useT, useLang } from "@/lib/i18n";
import OfferBanner from "@/components/site/OfferBanner";
import LangToggle from "@/components/site/LangToggle";

const NavBar = () => {
  const t = useT();
  const { lang } = useLang();
  const ctaCompact = lang === "fr" ? "RDV gratuit" : "Book a call";
  return (
  <div
    data-testid="site-nav"
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 60,
      background: "oklch(0.966 0.01 85 / 0.86)",
      backdropFilter: "blur(14px)",
      WebkitBackdropFilter: "blur(14px)",
      borderBottom: `1px solid ${C.border}`,
    }}
  >
    <div
      style={{
        maxWidth: 1360,
        margin: "0 auto",
        padding: "0 clamp(18px, 4vw, 52px)",
        height: 66,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 24,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => { e.preventDefault(); scrollTo("#top"); }}
          style={{ display: "flex", alignItems: "baseline", gap: 10, color: C.ink, whiteSpace: "nowrap" }}
        >
          <span style={{ fontFamily: serif, fontSize: 22, letterSpacing: "-0.01em" }}>Atelier Williams</span>
          <span className="tiny-hide" style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: "0.22em", textTransform: "uppercase", color: C.mut66 }}>
            {t("Studio")}
          </span>
        </a>
        <LangToggle />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span className="narrow-hide" style={{ display: "flex", alignItems: "center", gap: 7, fontFamily: mono, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.text48 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.green, animation: "aw-pulse 2.4s ease-in-out infinite" }} />
          {t("Available")}
        </span>
        <a
          href="#contact"
          data-testid="nav-cta"
          onClick={(e) => { e.preventDefault(); scrollTo("#contact"); }}
          className="aw-cta"
          style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", padding: "11px 18px", borderRadius: 2, background: C.ink, color: C.cream97, whiteSpace: "nowrap" }}
        >
          <span className="cta-full">{t("Free consultation")}</span>
          <span className="cta-compact">{ctaCompact}</span>
        </a>
      </div>
    </div>
  </div>
  );
};

const HERO_LINES = [
  { text: "One Developer.", em: false },
  { text: "Business Consultant.", em: false },
  { text: "Complete Ownership.", em: false },
];

const MaskLine = ({ children, delay, reduce }) => (
  <span style={{ display: "block", overflow: "hidden", paddingBottom: "0.06em" }}>
    <motion.span
      style={{ display: "block" }}
      initial={reduce ? false : { y: "44%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const t = useT();
  const reduce = useReducedMotion();
  const clock = useClock();
  const { scrollY } = useScroll();
  const gridY = useTransform(scrollY, [0, 600], [0, 90]);
  const gridOpacity = useTransform(scrollY, [0, 500], [1, 0.15]);

  return (
    <>
      <NavBar />
      <section
        id="top"
        style={{
          position: "relative",
          padding: "calc(66px + clamp(52px, 9vh, 110px)) 0 clamp(40px, 6vh, 72px)",
          overflow: "hidden",
        }}
      >
        <motion.div
          aria-hidden
          style={{
            position: "absolute",
            inset: "-10% 0 0 0",
            y: gridY,
            opacity: gridOpacity,
            backgroundImage:
              "linear-gradient(oklch(0.89 0.01 80 / 0.7) 1px, transparent 1px), linear-gradient(90deg, oklch(0.89 0.01 80 / 0.7) 1px, transparent 1px)",
            backgroundSize: "clamp(70px, 7vw, 110px) clamp(70px, 7vw, 110px)",
            maskImage: "linear-gradient(180deg, #000 0%, #000 52%, transparent 82%)",
            WebkitMaskImage: "linear-gradient(180deg, #000 0%, #000 52%, transparent 82%)",
            pointerEvents: "none",
          }}
        />
        <div style={{ position: "relative", maxWidth: 1360, margin: "0 auto", padding: "0 clamp(18px, 4vw, 52px)" }}>
          <motion.div
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            style={{ display: "flex", alignItems: "center", gap: 14, fontFamily: mono, fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase", color: C.mut66 }}
          >
            <motion.span
              initial={reduce ? false : { width: 0 }}
              animate={{ width: 26 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
              style={{ height: 1, background: C.accent, display: "block" }}
            />
            <span>{t("Shaun Williams — Senior full-stack developer")}</span>
          </motion.div>

          <h1
            style={{
              margin: "clamp(20px, 3vh, 34px) 0 0",
              fontFamily: serif,
              fontWeight: 400,
              fontSize: "clamp(42px, 7.4vw, 118px)",
              lineHeight: 0.92,
              letterSpacing: "-0.028em",
              maxWidth: "18ch",
              color: C.ink,
            }}
          >
            {HERO_LINES.map((l, i) => (
              <MaskLine key={i} delay={0.15 + i * 0.1} reduce={reduce}>
                {t(l.text)}
              </MaskLine>
            ))}
            <MaskLine delay={0.15 + HERO_LINES.length * 0.1} reduce={reduce}>
              <em style={{ fontStyle: "italic", color: C.accent }}>{t("Since 2019.")}</em>
            </MaskLine>
          </h1>

          <motion.div
            initial={reduce ? false : { y: 16 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.35 }}
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "clamp(28px, 4vw, 64px)", marginTop: "clamp(30px, 5vh, 56px)", alignItems: "end" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: "46ch" }}>
              <p style={{ margin: 0, fontFamily: sans, fontSize: "clamp(16px, 1.35vw, 19px)", lineHeight: 1.6, color: C.text40 }}>
                {t("Scope, strategy, art direction, full-stack development and deployment — owned end to end by one developer and business consultant.")}
              </p>
              <p style={{ margin: 0, fontFamily: sans, fontSize: "clamp(16px, 1.35vw, 19px)", lineHeight: 1.6, color: C.text40 }}>
                {t("Backed by several years of professional experience at Rolls-Royce and a higher-education background in business and management in England, I combine technical execution with commercial thinking.")}
              </p>
              <p style={{ margin: 0, fontFamily: sans, fontSize: "clamp(16px, 1.35vw, 19px)", lineHeight: 1.6, color: C.text40 }}>
                {t("Freelance based in Montpellier, working with public-sector organisations, international clients and startups.")}
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                <a href="#contact" data-testid="hero-cta-contact" onClick={(e) => { e.preventDefault(); scrollTo("#contact"); }} className="aw-cta" style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", padding: "16px 26px", borderRadius: 2, background: C.accent, color: "oklch(0.98 0.006 85)" }}>
                  {t("Let's discuss your project →")}
                </a>
                <a href="#work" data-testid="hero-cta-work" onClick={(e) => { e.preventDefault(); scrollTo("#work"); }} className="aw-cta" style={{ fontFamily: mono, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", padding: "16px 26px", borderRadius: 2, border: `1px solid ${C.mut82}`, color: C.ink }}>
                  {t("Selected work")}
                </a>
              </div>
              <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: "0.1em", color: C.mut66, textTransform: "uppercase" }}>
                Montpellier · {clock || "—"} {t("local")} · {t("remote EU/UK")}
              </div>
            </div>
          </motion.div>

          <OfferBanner style={{ marginTop: "clamp(28px, 4.5vh, 52px)" }} />

          <ProcessBar />
        </div>
      </section>
    </>
  );
};

const STEPS = ["Scope", "Art direction", "Build", "Deployment"];
const ProcessBar = () => {
  const t = useT();
  return (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}
    style={{
      marginTop: "clamp(44px, 8vh, 96px)",
      borderTop: `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`,
      padding: "clamp(18px, 2.6vh, 28px) 0",
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "clamp(10px, 2vw, 26px)",
      fontFamily: mono,
      fontSize: "clamp(11px, 1.15vw, 14px)",
      letterSpacing: "0.2em",
      textTransform: "uppercase",
    }}
  >
    {STEPS.map((s, i) => (
      <span key={s} style={{ display: "flex", alignItems: "center", gap: "clamp(10px, 2vw, 26px)" }}>
        <span style={{ color: i === 0 ? C.accent : C.mut72, letterSpacing: i === 0 ? "0.26em" : "0.2em" }}>{t(s)}</span>
        {i < STEPS.length - 1 && <span style={{ color: C.mut82 }}>→</span>}
      </span>
    ))}
  </motion.div>
  );
};

export default Hero;
